<script setup lang="ts">
import { api } from '@/api';
import {
  CalibrationDataOverviewDto,
  CreateOtherQaEquipmentDto,
  CreateQaEquipmentRtSystemDto,
  EntityWithCommentType,
  instanceOfUnicheckDeviceDto,
  instanceOfUnicheckSimulatorDto,
  PtwRegistrationState,
  type QaEquipmentRtSystemDto,
} from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import HistoryEvents from '@/components/history/HistoryEvents.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { TabDefinition, TabIndicatorState } from '@/components/Tabs/types.ts';
import { stopErrorPropagation } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { computed, ref, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { useDeleteQaEquipmentWithConfirmation } from '../utils';
import QaEquipmentFormCalibrationTab from './QaEquipmentFormCalibrationTab.vue';
import QaEquipmentFormAdministrativeTab from './QaEquipmentFormAdministrativeTab.vue';
import { EntityInfo } from '@/components/confirmationModals/types';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { useAmountComments } from '@/components/comments/useAmountComments';
import { useFormTab } from '@/components/forms/useFormTab';

const { t } = useTranslation();

const mode = useFormMode();

const router = useRouter();

const values = useFormValues<
  QaEquipmentRtSystemDto | CreateQaEquipmentRtSystemDto | CreateOtherQaEquipmentDto
>();

const qaEquipmentStore = useQaEquipmentStore();

const { commentsTabTitle, commentsChanged } = useAmountComments(values);

const isRegistered = computed(
  () =>
    'registrationState' in values.value &&
    values.value.registrationState === PtwRegistrationState.Registered,
);

// Calibration Tab
const calibrationData = ref<CalibrationDataOverviewDto>();

const deviceId = computed(() =>
  instanceOfUnicheckDeviceDto(values.value) || instanceOfUnicheckSimulatorDto(values.value)
    ? values.value.id
    : undefined,
);

watchEffect(async () => {
  if (values.value && deviceId.value) {
    const { result } = await api.qaEquipmentApi.getQaEquipmentCalibrationData(
      {
        id: deviceId.value,
      },
      [stopErrorPropagation],
    );

    if (result) {
      calibrationData.value = result;
    }
  }
});

const currentlyInstalledCalibration = computed(
  () => calibrationData.value?.existingCalibrations?.[0], // do not consider new calibrations
);

const calibrationIndicatorState = computed<TabIndicatorState | undefined>(() => {
  if (!isRegistered.value || currentlyInstalledCalibration.value?.calibrationState === undefined) {
    return undefined;
  }

  if (currentlyInstalledCalibration.value.calibrationState === 'Outdated') {
    return TabIndicatorState.WARNING;
  }

  if (currentlyInstalledCalibration.value.calibrationState === 'Invalid') {
    return TabIndicatorState.ERROR;
  }

  return undefined;
});

const { getIndicatorState } = useFormTab();

// Tabs
const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
        indicatorState: getIndicatorState(['name', 'serialNumber', 'dpin', 'attachments']),
        disabled: qaEquipmentStore.deRegistrationPending,
      },
      calibration: {
        hidden:
          mode.value === 'add' ||
          !(
            instanceOfUnicheckDeviceDto(values.value) ||
            instanceOfUnicheckSimulatorDto(values.value)
          ),
        disabled: qaEquipmentStore.deRegistrationPending,
        title: t('CalibrationAndDevice'),
        indicatorState: calibrationIndicatorState.value,
      },
      comments: {
        ...defaultPageTabs.comments,
        hidden: mode.value === 'add',
        title: commentsTabTitle.value,
        disabled: qaEquipmentStore.deRegistrationPending,
      },
      history: {
        ...defaultPageTabs.history,
        hidden: defaultPageTabs.history.hidden || mode.value === 'add',
        unmountTab: true,
        disabled: qaEquipmentStore.deRegistrationPending,
      },
    }) satisfies TabDefinition,
);

const { action: deleteAction } = useDeleteQaEquipmentWithConfirmation();

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);
  const id = 'id' in values.value && values.value.id;
  if (id) {
    return [
      {
        ...defaultPageActions.edit,
        to: {
          name: routenames.qaEquipment.edit,
          params: { id: id },
        },
        disabled: defaultPageActions.edit.disabled || qaEquipmentStore.deRegistrationPending,
      },
      {
        ...defaultPageActions.delete,
        callback: async () => {
          const { result } = await deleteAction({
            param: id,
            confirmationOptions: getDefaultDeleteConfirmationOptions(
              {
                ...(values.value as EntityInfo),
                typeName: 'DosimetryAndQaEquipmentEntity_one',
              },
              t,
            ),
          });
          if (result?.success) {
            router.push({ name: routenames.qaEquipment.overview });
          }
        },
        disabled: defaultPageActions.delete.disabled || qaEquipmentStore.deRegistrationPending,
      },
    ];
  } else {
    return [];
  }
});
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <template #administrative>
      <QaEquipmentFormAdministrativeTab />
    </template>

    <template #calibration>
      <QaEquipmentFormCalibrationTab :qaEquipment="values" :calibrationData="calibrationData" />
    </template>

    <template #comments>
      <Comments
        v-if="'id' in values && values.id"
        :id="values.id"
        :entityWithCommentType="EntityWithCommentType.QaEquipment"
        @change="commentsChanged"
      />
    </template>

    <template #history>
      <HistoryEvents
        v-if="'id' in values && values.id"
        :id="values.id"
        :historyApiFn="api.qaEquipmentApi.getQaEquipmentHistory.bind(api.qaEquipmentApi)"
      />
    </template>
  </BasePageNavigation>
</template>
